import { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import ToolbarFilterDropdown from '../components/ToolbarFilterDropdown';
import './Customers.css';
import './Engagements.css';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export default function Engagements() {
  const { canCreate } = useAuth();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pageSizeOpen, setPageSizeOpen] = useState(false);
  const pageSizeDdRef = useRef(null);
  const [filterMenuOpen, setFilterMenuOpen] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api
      .get('/engagements')
      .then(data => mounted && setRows(data || []))
      .catch(() => mounted && setRows([]))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!pageSizeOpen) return undefined;
    const close = () => setPageSizeOpen(false);
    const onDoc = e => {
      if (pageSizeDdRef.current && !pageSizeDdRef.current.contains(e.target)) close();
    };
    const onKey = e => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [pageSizeOpen]);

  const statusFilterOptions = useMemo(() => {
    const uniq = new Set(rows.map(r => r.status).filter(Boolean));
    const sorted = [...uniq].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    return [{ value: '', label: 'All Statuses' }, ...sorted.map(s => ({ value: s, label: s }))];
  }, [rows]);

  const clientFilterOptions = useMemo(() => {
    const uniq = new Set(rows.map(r => r.client_name).filter(Boolean));
    const sorted = [...uniq].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    return [{ value: '', label: 'All Clients' }, ...sorted.map(c => ({ value: c, label: c }))];
  }, [rows]);

  const filtered = useMemo(
    () =>
      rows.filter(r => {
        if (statusFilter && r.status !== statusFilter) return false;
        if (clientFilter && r.client_name !== clientFilter) return false;
        if (!search) return true;
        const q = search.toLowerCase();
        return `${r.rfp_num} ${r.sow_id || ''} ${r.client_name || ''} ${r.msa_id || ''}`
          .toLowerCase()
          .includes(q);
      }),
    [rows, search, statusFilter, clientFilter]
  );

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, clientFilter, pageSize]);

  useEffect(() => {
    const tp = Math.max(1, Math.ceil(filtered.length / pageSize));
    setPage(p => Math.min(p, tp));
  }, [filtered.length, pageSize]);

  const totalFiltered = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const offset = (safePage - 1) * pageSize;
  const pageRows = filtered.slice(offset, offset + pageSize);
  const rangeStart = totalFiltered === 0 ? 0 : offset + 1;
  const rangeEnd = totalFiltered === 0 ? 0 : Math.min(offset + pageSize, totalFiltered);

  const hasFilters = !!(search || statusFilter || clientFilter);
  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setClientFilter('');
  };

  return (
    <>
      <div className="page-header">
        <h1>Engagements</h1>
      </div>
      <div className="page-content engagements-list-page">
        <div className="customers-top">
          <div className="customers-stats" aria-label="Engagement counts">
            <div className="customers-stat">
              <div className="customers-stat-label">In list</div>
              <div className="customers-stat-value">{loading ? '—' : rows.length.toLocaleString()}</div>
            </div>
            <div className="customers-stat">
              <div className="customers-stat-label">Match filters</div>
              <div className="customers-stat-value">{loading ? '—' : filtered.length.toLocaleString()}</div>
            </div>
          </div>
          <div className="customers-top-right">
            {hasFilters && (
              <button type="button" className="btn btn-outline btn-sm" onClick={clearFilters}>
                Clear filters
              </button>
            )}
            {canCreate('engagements') && (
              <Link to="/engagements/new" className="btn btn-primary btn-sm customers-new-btn">
                + New Engagement
              </Link>
            )}
          </div>
        </div>

        {rows.length > 0 && filtered.length < rows.length && (
          <div className="customers-filter-hint" role="status">
            <span>
              Filters narrow the table to <strong>{filtered.length}</strong> of <strong>{rows.length}</strong>{' '}
              engagement{rows.length === 1 ? '' : 's'} in your current list.
            </span>
            <button type="button" className="btn btn-outline btn-sm" onClick={clearFilters}>
              Clear filters
            </button>
          </div>
        )}

        <div className="customers-toolbar">
          <div className="customers-toolbar-left">
            <div className="customers-search">
              <span className="customers-search-icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path d="M16.3 16.3 21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </span>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by RFP, SOW, MSA, or client…"
                aria-label="Search engagements"
              />
            </div>
            <div className="customers-filters">
              <ToolbarFilterDropdown
                id="eng-filter-status"
                labelText="Status"
                ariaLabel="Filter by status"
                value={statusFilter}
                onChange={setStatusFilter}
                placeholder="All Statuses"
                options={statusFilterOptions}
                menuKey="eng-status"
                openMenu={filterMenuOpen}
                setOpenMenu={setFilterMenuOpen}
              />
              <ToolbarFilterDropdown
                id="eng-filter-client"
                labelText="Client"
                ariaLabel="Filter by client"
                value={clientFilter}
                onChange={setClientFilter}
                placeholder="All Clients"
                options={clientFilterOptions}
                menuKey="eng-client"
                openMenu={filterMenuOpen}
                setOpenMenu={setFilterMenuOpen}
                wide
              />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="engagements-list-table">
            <table>
              <thead>
                <tr>
                  <th>RFP number</th>
                  <th>SOW</th>
                  <th>MSA</th>
                  <th>Client</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map(r => (
                  <tr key={r.rfp_num}>
                    <td>
                      <Link to={`/engagements/${encodeURIComponent(r.rfp_num)}`} className="link">
                        {r.rfp_num}
                      </Link>
                    </td>
                    <td>
                      <Link to={`/sow/${r.sow_id}`} className="link" title={r.sow_id}>
                        {r.sow_id}
                      </Link>
                    </td>
                    <td>
                      <Link to={`/msa/${r.msa_id}`} className="link" title={r.msa_id}>
                        {r.msa_id}
                      </Link>
                    </td>
                    <td title={r.client_name}>{r.client_name}</td>
                    <td>{r.from_date}</td>
                    <td>{r.to_date}</td>
                    <td>
                      <span className={`badge badge-${(r.status || '').toLowerCase()}`}>{r.status}</span>
                    </td>
                  </tr>
                ))}
                {!filtered.length && (
                  <tr>
                    <td colSpan="7" className="engagements-list-empty">
                      {loading ? 'Loading engagements…' : 'No engagements match the filters.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {!loading && totalFiltered > 0 && (
            <div className="customers-pagination" role="navigation" aria-label="Table pagination">
              <div className="customers-pagination-summary">
                <span className="customers-pagination-summary-label">Showing</span>
                <span className="customers-pagination-summary-range">
                  <strong className="tabular-nums">{rangeStart}</strong>
                  <span className="customers-pagination-summary-dash">–</span>
                  <strong className="tabular-nums">{rangeEnd}</strong>
                  <span className="customers-pagination-summary-of">of</span>
                  <strong className="tabular-nums">{totalFiltered.toLocaleString()}</strong>
                  <span className="customers-pagination-summary-unit">rows</span>
                </span>
              </div>
              <div className="customers-pagination-controls">
                <div className="customers-pagination-page-size" ref={pageSizeDdRef}>
                  <span className="customers-pagination-label" id="engagements-page-size-label">
                    Rows per page
                  </span>
                  <div className="customers-page-size-dd-inner">
                    <button
                      type="button"
                      className={`customers-page-size-trigger ${pageSizeOpen ? 'customers-page-size-trigger-open' : ''}`}
                      aria-haspopup="listbox"
                      aria-expanded={pageSizeOpen}
                      aria-label={`Rows per page, current value ${pageSize}`}
                      onClick={() => setPageSizeOpen(o => !o)}
                    >
                      <span>{pageSize}</span>
                      <span className="customers-page-size-chevron" aria-hidden>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </span>
                    </button>
                    {pageSizeOpen && (
                      <ul className="customers-page-size-menu" role="listbox" aria-labelledby="engagements-page-size-label">
                        {PAGE_SIZE_OPTIONS.map(n => (
                          <li
                            key={n}
                            role="option"
                            aria-selected={pageSize === n}
                            className={
                              pageSize === n
                                ? 'customers-page-size-option customers-page-size-option-active'
                                : 'customers-page-size-option'
                            }
                            onMouseDown={e => {
                              e.preventDefault();
                              setPageSize(n);
                              setPageSizeOpen(false);
                            }}
                          >
                            {n}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <div className="customers-pagination-pager" role="group" aria-label="Page navigation">
                  <button
                    type="button"
                    className="customers-pagination-step"
                    disabled={safePage <= 1}
                    onClick={() => setPage(1)}
                    aria-label="First page"
                    title="First page"
                  >
                    <span className="customers-pagination-step-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 19l-7-7 7-7" />
                        <path d="M18 19l-7-7 7-7" />
                      </svg>
                    </span>
                    <span>First</span>
                  </button>
                  <button
                    type="button"
                    className="customers-pagination-step"
                    disabled={safePage <= 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    aria-label="Previous page"
                    title="Previous page"
                  >
                    <span className="customers-pagination-step-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 18l-6-6 6-6" />
                      </svg>
                    </span>
                    <span>Prev</span>
                  </button>
                  <div className="customers-pagination-page-chip">
                    <span className="customers-pagination-page-label">Page</span>
                    <span className="customers-pagination-page-current tabular-nums">{safePage}</span>
                    <span className="customers-pagination-page-sep">/</span>
                    <span className="customers-pagination-page-total tabular-nums">{totalPages}</span>
                  </div>
                  <button
                    type="button"
                    className="customers-pagination-step"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    aria-label="Next page"
                    title="Next page"
                  >
                    <span>Next</span>
                    <span className="customers-pagination-step-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 18l6-6-6-6" />
                      </svg>
                    </span>
                  </button>
                  <button
                    type="button"
                    className="customers-pagination-step"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage(totalPages)}
                    aria-label="Last page"
                    title="Last page"
                  >
                    <span>Last</span>
                    <span className="customers-pagination-step-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M13 5l7 7-7 7" />
                        <path d="M6 5l7 7-7 7" />
                      </svg>
                    </span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
